import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useProjectTranslations} from "@i18n/i18nUtils";
import {findFirstSection, getCmsPage} from "@lib/Cms/CmsUtils";
import {SECTION_TYPE} from "@lib/Cms/CmsModels";
import {useProduct} from "@lib/Catalog/CatalogHooks";
import Page from "@components/Page/Page";
import PageContent from "@components/Page/PageContent";
import PageSection from "@components/Page/PageSection";
import PreFooter from "@components/Prefooter/PreFooter";
import ResponsivePicture from "@components/ResponsivePicture/ResponsivePicture";
import {useMainWallet} from "@lib/Wallet/WalletHooks";
import {Button, Disclosure, DisclosurePanel, Heading} from "react-aria-components";
import {ChevronUpIcon} from "@components/Icons/ChevronUpIcon";
import "./ProductPage.scss";
import {formatPoints} from "@commons/Utils";
import {ChevronRightIcon} from "@components/Icons/ChevronRightIcon";
import {useModalStore} from "@store/ModalStore";
import {useShallow} from "zustand/react/shallow";
import RedeemRequestModalContent from "@pages/Product/Components/RedeemRequestModalContent";
import {buildRoute} from "@router/RouterUtils";
import {AppRoute} from "@router/RoutingModels";

export default function ProductPage() {

    const {productId} = useParams<{ productId: string }>();
    const navigate = useNavigate();

    const {t} = useProjectTranslations();

    const {openModal, closeModal} = useModalStore(useShallow(s => ({
        openModal: s.open,
        closeModal: s.close,
    })));

    const {sections} = useMemo(() => getCmsPage("productDetails") ?? {}, []);

    const defaultSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.DEFAULT), [sections]);
    const preFooterSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.PRE_FOOTER), [sections]);

    const mediaSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.MEDIA), [sections]);
    const brandSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.BRAND), [sections]);
    const titleSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.TITLE), [sections]);
    const categorySection = useMemo(() => findFirstSection(sections, SECTION_TYPE.CATEGORY), [sections]);
    const formSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.REDEEM_FORM), [sections]);
    const descriptionSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.DESCRIPTION), [sections]);
    const brandDescriptionSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.BRAND_DESCRIPTION), [sections]);
    const tAndCSection = useMemo(() => findFirstSection(sections, SECTION_TYPE.T_AND_C), [sections]);

    const {data: product = null, isLoading: isProductLoading, isError: isProductError} = useProduct(productId);

    const {data: wallet, isLoading: isWalletLoading} = useMainWallet();

    const [quantity, setQuantity] = useState<number>(1);

    const maxQty = useMemo(() => {
        if (!wallet || !product) return 1;
        if (!product.purchasable) return 1;
        const unitCost = Number(product.points ?? 0);
        const balance = Number(wallet.number ?? 0);
        if (!unitCost || unitCost <= 0) return 1;
        return Math.max(0, Math.floor(balance / unitCost));
    }, [wallet, product]);

    useEffect(() => {
        if (maxQty <= 0) {
            setQuantity(1);
            return;
        }
        setQuantity((q) => Math.min(Math.max(q, 1), maxQty));
    }, [maxQty]);

    const canRedeem = useMemo(() => {
        if (!wallet || !product) return false;
        if (!product.purchasable) return false;
        if (maxQty <= 0) return false;
        return quantity * product.points <= wallet.number;
    }, [wallet, product, quantity, maxQty]);

    const totalCost = useMemo(() => {
        if (!product) return 0;
        return quantity * Number(product.points ?? 0);
    }, [product, quantity]);

    const canDecrement = quantity > 1;
    const canIncrement = maxQty > 0 && quantity < maxQty;

    const decQty = useCallback(() => {
        setQuantity((q) => Math.max(1, q - 1));
    }, []);

    const incQty = useCallback(() => {
        if (maxQty <= 0) return;
        setQuantity((q) => Math.min(maxQty, q + 1));
    }, [maxQty]);

    const onRedeem = useCallback(async () => {
        if (!product || !wallet) return;
        if (!canRedeem) return;

        await openModal({
            tone: "success",
            title: t("redemption:modal:title"),
            content: (
                <RedeemRequestModalContent
                    product={product}
                    quantity={quantity}
                    totalCost={totalCost}
                    onSuccessClick={() => {
                        closeModal();
                        navigate(buildRoute(AppRoute.REWARDS_HISTORY));
                    }}
                />
            )
        });

    }, [canRedeem, product, wallet, quantity, totalCost]);

    return (
        <>
            <Page>
                <PageContent>

                    <PageSection bg={defaultSection?.options?.bgType}>

                        {isProductLoading &&
                            <p className="loading">{t("loading")}</p>
                        }

                        {isProductError &&
                            <p className="error">{t("error")}</p>
                        }

                        {!!product && !isProductLoading && !isProductError &&
                            <div className={"product"}>

                                {!!mediaSection &&
                                    <div className="product__media" data-has-image={!!product.image}>
                                        {product.image &&
                                            <ResponsivePicture images={[product.image]}/>
                                        }
                                    </div>
                                }

                                <div className="product__content">

                                    {!!brandSection && (!!product.brand || !!product.brandLogo) &&
                                        <div className={"product__brand"}>
                                            {!!product.brandLogo &&
                                                <div className={"product__brand__logo"}>
                                                    <img src={product.brandLogo} alt={""}/>
                                                </div>
                                            }
                                            {!!product.brand &&
                                                <div className={"product__brand__name"}>
                                                    <h4 className="tp-xs">{product.brand}</h4>
                                                </div>
                                            }
                                        </div>
                                    }

                                    {!!titleSection &&
                                        <div className="product__main">
                                            <h1 className={"product__title tp-xl semibold"}>{product.title}</h1>
                                            {!!product.shortDescription &&
                                                <p className="product__short_description"
                                                   dangerouslySetInnerHTML={{__html: product.shortDescription}}/>
                                            }
                                        </div>
                                    }

                                    {!!categorySection && !!product.category &&
                                        <p className="product__category tp-xs semibold">
                                            {product.category}
                                        </p>
                                    }

                                    {!!formSection && (
                                        <div
                                            className="product__actions"
                                            data-redeemable={canRedeem}
                                            data-purchasable={product.purchasable}
                                        >
                                            {product.purchasable ? (
                                                <>
                                                    <div className={"product__quantity-selection"}>
                                                        <div className="quantityRow">
                                                            <div className="pointsPill"
                                                                 aria-label={t("product:points_label")}>
                                                                <span className="pointsPill__icon"
                                                                      aria-hidden="true">★</span>
                                                                <span
                                                                    className="pointsPill__value">{formatPoints(product.points)}</span>
                                                            </div>

                                                            <div
                                                                className="quantityStepper"
                                                                role="group"
                                                                aria-label={t("product:form:quantity_label")}
                                                            >
                                                                <Button
                                                                    className="quantityStepper__btn"
                                                                    onPress={decQty}
                                                                    isDisabled={!canDecrement}
                                                                    aria-label={t("product:form:quantity_decrease")}
                                                                >
                                                                    <span aria-hidden="true">−</span>
                                                                </Button>

                                                                <div className="quantityStepper__value"
                                                                     aria-live="polite">
                                                                    {quantity}
                                                                </div>

                                                                <Button
                                                                    className="quantityStepper__btn"
                                                                    onPress={incQty}
                                                                    isDisabled={!canIncrement}
                                                                    aria-label={t("product:form:quantity_increase")}
                                                                >
                                                                    <span aria-hidden="true">+</span>
                                                                </Button>
                                                            </div>
                                                        </div>

                                                        <div className="quantityMeta tp-xs">
                                                          <span className="quantityMeta__total">
                                                            {t("product:form:total_points")}: <strong>{formatPoints(totalCost)}</strong>
                                                          </span>
                                                            {!!wallet && (
                                                                <span className="quantityMeta__balance">
                                                              {t("product:form:wallet_balance")}: <strong>{wallet.number}</strong>
                                                            </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className={"product__redeem-button"}>
                                                        <button
                                                            className={`cta cta--medium cta--primary`}
                                                            disabled={!canRedeem || isWalletLoading}
                                                            onClick={onRedeem}
                                                        >
                                                            <span
                                                                className="label">{t("product:form:redeem_label")}</span>
                                                            <span className="icon">
                                                                <ChevronRightIcon/>
                                                            </span>
                                                        </button>

                                                        {!canRedeem && (
                                                            <p className="tp-xs semibold color-primary product__redeem-error">
                                                                {maxQty <= 0
                                                                    ? t("product:form:error:not_enough_points")
                                                                    : t("product:form:error:not_redeemable")}
                                                            </p>
                                                        )}
                                                    </div>
                                                </>
                                            ) : (
                                                <p className="error tp-xs">{t("product:error:not_available")}</p>
                                            )}
                                        </div>
                                    )}

                                    <div className="product__descriptions">

                                        {!!descriptionSection && !!product.longDescription &&
                                            <div className="product__collapsable-section">
                                                <Disclosure defaultExpanded={true}>
                                                    <Heading level={6}>
                                                        <Button slot="trigger" className="disclosure-button">
                                                            <strong
                                                                className={"tp-xs semibold"}>{descriptionSection?.title ?? ""}</strong>
                                                            <span><ChevronUpIcon/></span>
                                                        </Button>
                                                    </Heading>
                                                    <DisclosurePanel>
                                                        <p className={"tp-xs"}
                                                           dangerouslySetInnerHTML={{__html: product.longDescription}}/>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </div>
                                        }

                                        {!!brandDescriptionSection && !!product.brandDescription &&
                                            <div className="product__collapsable-section">
                                                <Disclosure>
                                                    <Heading level={5}>
                                                        <Button slot="trigger" className="disclosure-button">
                                                            <strong
                                                                className={"tp-xs semibold"}>{brandDescriptionSection?.title ?? ""}</strong>
                                                            <span><ChevronUpIcon/></span>
                                                        </Button>
                                                    </Heading>
                                                    <DisclosurePanel>
                                                        <p className={"tp-xs"}
                                                           dangerouslySetInnerHTML={{__html: product.brandDescription}}/>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </div>
                                        }

                                        {!!tAndCSection && !!product.terms &&
                                            <div className="product__collapsable-section">
                                                <Disclosure>
                                                    <Heading level={5}>
                                                        <Button slot="trigger" className="disclosure-button">
                                                            <strong
                                                                className={"tp-xs semibold"}>{tAndCSection?.title ?? ""}</strong>
                                                            <span><ChevronUpIcon/></span>
                                                        </Button>
                                                    </Heading>
                                                    <DisclosurePanel>
                                                        <p className={"tp-xs"}
                                                           dangerouslySetInnerHTML={{__html: product.terms}}/>
                                                    </DisclosurePanel>
                                                </Disclosure>
                                            </div>
                                        }

                                    </div>

                                </div>

                            </div>
                        }

                    </PageSection>

                </PageContent>

            </Page>

            {!!preFooterSection && <PreFooter cms={preFooterSection}/>}

        </>
    );

}
